import { ApiTransport } from './ApiTransport';
import type { PaginatedResult, SkillSummary } from './types';
import { readSkillResponse, SkillReadError } from './skill-read';
import type { SpassuCodeApi } from './SpassuCodeApiContract';

type SkillFilters = NonNullable<Parameters<SpassuCodeApi['listSkills']>[0]>;
type SkillInput = Parameters<SpassuCodeApi['createSkill']>[0];
type SkillUpdate = Parameters<SpassuCodeApi['updateSkill']>[1];

export class SkillsClient {
  constructor(private readonly transport: ApiTransport) {}

  async listSkills(
    filters: SkillFilters = {},
  ): Promise<PaginatedResult<SkillSummary>> {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.origin) params.set('origin', filters.origin);
    if (filters.phase) params.set('phase', filters.phase);
    if (filters.page) params.set('page', String(filters.page));
    if (filters.limit) params.set('limit', String(filters.limit));
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await this.transport.request(`/agents/skills${qs}`);
    return response.json();
  }

  async getSkill(id: string): Promise<SkillSummary> {
    try {
      const response = await this.transport.request(
        `/agents/skills/${encodeURIComponent(id)}`,
      );
      return await readSkillResponse(response, id);
    } catch (error) {
      if (error instanceof SkillReadError) throw error;
      throw new SkillReadError('load');
    }
  }

  async createSkill(data: SkillInput): Promise<SkillSummary> {
    const response = await this.transport.request(`/agents/skills`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return response.json();
  }

  async updateSkill(id: string, data: SkillUpdate): Promise<SkillSummary> {
    const response = await this.transport.request(`/agents/skills/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return response.json();
  }

  async signSkill(id: string): Promise<SkillSummary> {
    const response = await this.transport.request(`/agents/skills/${id}/sign`, {
      method: 'POST',
    });
    return response.json();
  }

  async submitSkillForReview(id: string): Promise<SkillSummary> {
    const response = await this.transport.request(
      `/agents/skills/${id}/submit-review`,
      { method: 'POST' },
    );
    return response.json();
  }

  async approveSkill(id: string): Promise<SkillSummary> {
    const response = await this.transport.request(
      `/agents/skills/${id}/approve`,
      { method: 'POST' },
    );
    return response.json();
  }

  async submitCommunitySkill(data: SkillInput): Promise<SkillSummary> {
    const response = await this.transport.request(
      `/agents/skills/community-submit`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      },
    );
    return response.json();
  }
}
